/**
 * A support request's life.
 *
 * Pure, and shared in shape with the backend's `types/support.ts`. The rules
 * are the enforcement; this decides what a screen says about a request, which
 * is not the same question and has one wrinkle the enforcement does not care
 * about: **an approval whose window has closed is still stored as approved.**
 * The decision happened, and a clock should not rewrite the record of it.
 */

export type SupportRequestStatus = "pending" | "approved" | "denied" | "revoked";
export type SupportRequestState = SupportRequestStatus | "expired";

export const SUPPORT_DURATIONS_HOURS = [1, 8, 24] as const;
export type SupportDurationHours = (typeof SUPPORT_DURATIONS_HOURS)[number];

export interface SupportRequest {
  id: string;
  institutionId: string;
  requestedBy: string;
  requestedByEmail: string;
  reason: string;
  durationHours: SupportDurationHours;
  status: SupportRequestStatus;
  createdAt: string;
  decidedAt?: string;
  decidedBy?: string;
  expiresAt?: string;
  revokedAt?: string;
}

export function stateOf(request: SupportRequest, now: number): SupportRequestState {
  if (request.status !== "approved") return request.status;
  if (!request.expiresAt) return "approved";
  return Date.parse(request.expiresAt) > now ? "approved" : "expired";
}

export function isActive(request: SupportRequest, now: number): boolean {
  return stateOf(request, now) === "approved";
}

/**
 * How long is left, in whole minutes.
 *
 * Minutes rather than a countdown to the second: an admin reading "37 minutes"
 * knows whether to start something; one reading "36:41" watches it.
 */
export function minutesRemaining(request: SupportRequest, now: number): number {
  if (!request.expiresAt) return 0;
  const remaining = Date.parse(request.expiresAt) - now;
  return remaining <= 0 ? 0 : Math.ceil(remaining / 60_000);
}

/** The one request, if any, that is currently letting somebody in. */
export function activeRequest(
  requests: readonly SupportRequest[],
  now: number
): SupportRequest | undefined {
  return requests.find((request) => isActive(request, now));
}

export const STATE_KEY: Record<SupportRequestState, string> = {
  pending: "support.state.pending",
  approved: "support.state.approved",
  denied: "support.state.denied",
  revoked: "support.state.revoked",
  expired: "support.state.expired",
};

export const STATE_TONE: Record<SupportRequestState, string> = {
  pending: "text-warning",
  approved: "text-positive",
  denied: "text-ink-muted",
  revoked: "text-ink-muted",
  expired: "text-ink-faint",
};

/**
 * Whether a reason is enough to decide on.
 *
 * Twenty characters, matching the backend. The owner reading it has to decide
 * whether to let an outsider into their institution's data, and "support" is
 * not information anybody can decide on.
 */
export const MIN_REASON_LENGTH = 20;

export function reasonIsSufficient(reason: string): boolean {
  return reason.trim().length >= MIN_REASON_LENGTH && reason.trim().length <= 400;
}
